// Import React
import React, { useState } from "react";

// Import hooks
import useAiQueueManager from "pages/ai_image_generation/hooks/useAiQueueManager";

// Import the reusable select field
import PodDesignAiSelectField from "./PodDesignAiSelectField";

// Import the reusable image option grid
import {PodDesignAiImageAspectRatioOptionGrid, PodDesignAiImageStyleOptionGrid} from "./PodDesignAiImageOptionGrid";
import CreateButton from "global/components/CreateButton";

// Import components
import AiModelsInfoTable from "pages/ai_image_generation/components/AiModelsInfoTable";
import RadialProgress from "global/components/RadialProgress";
import { CloseIcon } from "global/strich_icons/StrichIcons";



// Export the prompt form component
export default function PodDesignAiPromptForm({
    prompt,
    setPrompt,
    title,
    setTitle,
    designType,
    setDesignType,
    selectedModelId,
    setSelectedModelId,
    selectedStyleId,
    setSelectedStyleId,
    tagDescribeMode,
    setTagDescribeMode,
    selectedAspectRatio,
    setSelectedAspectRatio,
    modelOptions,
    styleOptions,
    aspectRatioOptions,
    designTypeOptions,
    tagDescribeOptions,

    onGenerate,
    onReset,
    canGenerate,
    isGenerating,

    canOptimise,
    onOptimise,
    isOptimising,

    handleSubmitDesign,
    canSubmit,

    isLoadingSetupData,
    generateError,
    setupError,

    currentStep,
    generatedImage,
    optimisedImage,
    previewBackgroundColours,
    
    formSettingsMatchImageSettings,

}) {



    // This gets the functions from ai queue manager (in ai studio) for showing colour and progress values
    const {getQueueItemProgressValue, getQueueItemProgressColor} = useAiQueueManager({});

    // This opens the pop up with ai models info 
    const [showAiModelsTable, setShowAiModelsTable] = useState(false);

    // This stores the user prompt
    const [selectedPreviewBackgroundColour, setSelectedPreviewBackgroundColour] = useState("Transparent");
     

    // Manually handle updating the selected aspect ratio on design type change
    const handleDesignTypeChange = (value) => {

        if (value === "standalone") {

            // Set the aspect ratio
            setSelectedAspectRatio("square");

        } else {

            // Set the aspect ratio
            setSelectedAspectRatio("portrait");

        }

        // Set the design type
        setDesignType(value);

    }

    // Handle Enter inside the prompt textarea
    const handlePromptKeyDown = (event) => {

        // Only act on Enter
        if (event.key !== "Enter") { return; }

        // Allow Shift+Enter to insert a new line
        if (event.shiftKey) { return; }

        // Prevent the default newline behaviour
        event.preventDefault();

        // Do not generate while disabled or already busy
        if (!canGenerate || isGenerating || isOptimising) { return; }

        // Start image generation
        onGenerate();

    };

        // Set the prompt placeholder text based on the selected design type
    const prompt_placeholder = designType === "edge-to-edge"
        ? "Edge to Edge designs work better with longer prompts e.g. A lone heroine with blonde hair, long hair, wavy hair, leather pants, a corset, bare shoulders, and ankle boots, a gold necklace with pendant, gold bangle, standing in the middle of a road in a cyberpunk city, Matrix atmosphere, ultra cinematic, dark and epic. Focus on the woman, full length view."
        : "Standalone designs work better with short, simple prompts e.g. A goat on a boat";

    // Return the prompt form UI
    return (

        <div className="podDesignAiPromptForm">

            {/* This is the settings panel */}
            <div className="podDesignAiPromptForm__settingsPanel">


                {/* This is the prompt field group */}
                <div className="podDesignAiPromptForm__fieldGroup">

                    {/* This is the prompt label */}
                    <label className="podDesignAiPromptForm__label">

                        Design Prompt

                    </label>

                    {/* This is the prompt textarea */}
                    <textarea
                        className="podDesignAiPromptForm__textarea"
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        onKeyDown={handlePromptKeyDown}
                        placeholder={prompt_placeholder}
                        rows={3}
                    />

                </div>

                <div className="podDesignAiPromptForm__designSettingsTop">

                    {/* This is the model field */}
                    <PodDesignAiSelectField
                        label="Model"
                        value={selectedModelId}
                        onChange={setSelectedModelId}
                        disabled={isLoadingSetupData}
                        options={modelOptions}
                        aiModelsInfoTable={AiModelsInfoTable}
                        setShowAiModelsTable={setShowAiModelsTable}
                    />

                    {/* This is the design type field */}
                    <PodDesignAiSelectField
                        label="Design Type"
                        value={designType}
                        onChange={
                            
                            handleDesignTypeChange
                        
                        }
                        disabled={isLoadingSetupData}
                        options={designTypeOptions}
                    />
                   
                    {/* This is the aspect ratio field slot */}
                    <div className={`podDesignAiPromptForm__aspectRatioSlot ${designType === "edge-to-edge" ? "podDesignAiPromptForm__aspectRatioSlot--visible" : "podDesignAiPromptForm__aspectRatioSlot--hidden"}`}>

                        {/* This is the aspect ratio image selector */}
                        <PodDesignAiImageAspectRatioOptionGrid
                            title="Aspect Ratio"
                            options={aspectRatioOptions}
                            selectedValue={selectedAspectRatio}
                            onSelect={setSelectedAspectRatio}
                            disabled={isLoadingSetupData || designType !== "edge-to-edge"}
                        />

                    </div>

                </div>

                {showAiModelsTable && 
                
                    <div className="popup-overlay">
            
                        <div className="popup-container popup-container--small" style={{height: "auto"}}>

                            {/* This is the modal header */}
                            <div className="popup__header">

                                {/* This is the title area */}
                                <div className="popup__headerLeft">

                                    {/* This is the parent title */}
                                    <h2 className="popup__title">

                                        AI Models

                                    </h2>

                                    {/* This is the parent subtitle */}
                                    <p className="popup__subtitle">

                                        Compare speed, quality, text support, and ideal use cases at a glance.

                                    </p>

                                </div>

                                {/* This is the header actions */}
                                <div className="popup__headerRight">

                                    {/* This is the close button */}
                                    <button type="button" className="popup__close" onClick={() => setShowAiModelsTable(false)} title="Close">
                                        <CloseIcon />
                                    </button>

                                </div>

                            </div>


                            <div className="popup-content popup-content__body" style={{maxHeight: "700px", padding: "20px"}}>
                   
                                <AiModelsInfoTable />

                            </div>

                        </div>

                    </div>
                                       
                }

                {/* This is the style image selector */}
                <PodDesignAiImageStyleOptionGrid
                    title="Style"
                    options={styleOptions}
                    selectedValue={selectedStyleId}
                    onSelect={setSelectedStyleId}
                    disabled={isLoadingSetupData}
                />

                {/* This shows any setup error */}
                {setupError && (
                    <div className="podDesignAiPromptForm__error">
                        {setupError}
                    </div>
                )}

            </div>

            {/* This is the main prompt panel */}
            <div className="podDesignAiPromptForm__mainPanel">

                {/* This is the prompt heading 
                <h3 className="podDesignAiPromptForm__panelTitle">
                    Describe & Generate
                </h3>
                */}


                {/* This is the preview label */}
                <label className="podDesignAiPromptForm__label podDesignAiPromptForm__previewLabel">

                    Design Preview

                </label>

                {/* This is the fixed preview panel */}
                <div className="podDesignAiPromptForm__staticPreviewPanel">

                    {/* Work out which image should be shown in the preview panel */}
                    {(() => {

                        // Show the optimised image on review, otherwise show the generated image
                        const preview_image = currentStep === "review" && optimisedImage ? optimisedImage : generatedImage;

                        // Check whether there is an image available to preview
                        const has_preview_image = Boolean(preview_image?.image_url);

                        // Check whether the UI is currently generating
                        const show_generating_state = currentStep === "generating";

                        // Check whether the UI is currently optimising
                        const show_optimising_state = currentStep === "optimising";

                        return (

                            <>

                                {/* This is the fixed preview body */}
                                <div className="podDesignAiPromptForm__staticPreviewBody transparency-bg">

                                    {/* Show the preview image when available and not currently processing */}
                                                                        {has_preview_image && !show_generating_state && !show_optimising_state && (

                                        <div className="podDesignAiPromptForm__staticPreviewMedia">

                                            {/* This is the preview image */}
                                            <img
                                                className="podDesignAiPromptForm__staticPreviewImage"
                                                src={preview_image.image_url}
                                                alt={title || preview_image.name || "Generated design"}
                                                style={{
                                                    backgroundColor: `${selectedPreviewBackgroundColour}`,
                                                    border: `5px solid ${selectedPreviewBackgroundColour}`,
                                                }}
                                            />

                                            {/* Hide the background colour picker for now without removing the code */}
                                            {false && designType === "standalone" && (

                                                <div className="preview-background-picker">

                                                    {previewBackgroundColours?.map((colour) => {

                                                        const isTransparent = colour.label === "Transparent";

                                                        return (

                                                            <div
                                                                key={colour.label}
                                                                title={colour.label}
                                                                className="preview-background-picker__colour"
                                                                onClick={() => setSelectedPreviewBackgroundColour(colour.label)}
                                                                style={{
                                                                    backgroundColor: `${colour.colour}`,
                                                                    border: colour.label === selectedPreviewBackgroundColour ? `5px solid var(--primary-color)` : isTransparent ? `2px solid var(--primary-color)` : `5px solid ${colour.colour}`,
                                                                }}
                                                            >
                                                            </div>

                                                        );

                                                    })}

                                                </div>

                                            )}

                                        </div>

                                    )}



                                    {/* Show the generating state inside the fixed preview body */}
                                    {show_generating_state && (

                                        <div className="podDesignAiPromptForm__staticPreviewStatus">

                                            {/* This is the progress ring area */}
                                            <div className="ai-in-progress-progress-content">

                                                <div className="ai-image-progress-circle">

                                                    <RadialProgress
                                                        value={getQueueItemProgressValue(generatedImage)}
                                                        direction={"clockwise"}
                                                        size={200}
                                                        progressColor={getQueueItemProgressColor(generatedImage)}
                                                        bgColor={"var(--input-border, #b3b3b3)"}
                                                    />

                                                    <div className="ai-image-progress-text">

                                                        <div>{generatedImage?.status}</div>
                                                        <div>{generatedImage?.run_by}</div>

                                                    </div>

                                                </div>

                                            </div>

                                            {/* This is the generating title */}
                                            <h2 className="podDesignAiPage__loadingTitle">
                                                Generating Your Design
                                            </h2>

                                            {/* This is the generating helper text */}
                                            <p className="podDesignAiPage__loadingText">
                                                We’re generating your design image.<br></br> If you're spending a while waiting, consider upgrading your subscription for faster generation.
                                            </p>

                                        </div>

                                    )}



                                    {/* Show the optimising state inside the fixed preview body */}
                                    {show_optimising_state && (

                                        <div className="podDesignAiPromptForm__staticPreviewStatus">

                                            {/* This is the progress ring area */}
                                            <div className="ai-in-progress-progress-content">

                                                <div className="ai-image-progress-circle">

                                                    <RadialProgress
                                                        value={getQueueItemProgressValue(optimisedImage)}
                                                        direction={"clockwise"}
                                                        size={200}
                                                        progressColor={getQueueItemProgressColor(optimisedImage)}
                                                        bgColor={"var(--input-border, #b3b3b3)"}
                                                    />

                                                    <div className="ai-image-progress-text">

                                                        <div>{optimisedImage?.status}</div>
                                                        <div>{optimisedImage?.run_by}</div>

                                                    </div>

                                                </div>

                                            </div>

                                            {/* This is the optimising title */}
                                            <h2 className="podDesignAiPage__loadingTitle">
                                                Optimising Your Design
                                            </h2>

                                            {/* This is the optimising helper text */}
                                            <p className="podDesignAiPage__loadingText">
                                                We’re preparing your design for maximum print quality. It's worth the wait.
                                            </p>

                                        </div>

                                    )}



                                    {/* Show the placeholder before any image exists */}
                                    {!has_preview_image && !show_generating_state && !show_optimising_state && (

                                        <div className="podDesignAiPromptForm__staticPreviewPlaceholder">

                                            {/* This is the placeholder title */}
                                            <h3 className="podDesignAiPromptForm__staticPreviewTitle">
                                                Image Preview
                                            </h3>

                                            {/* This is the placeholder helper text */}
                                            <p className="podDesignAiPromptForm__staticPreviewText">
                                                Generate an image and it will appear here.
                                            </p>

                                        </div>

                                    )}

                                </div>



                                {/* This is the fixed action bar */}
                                <div className="podDesignAiPromptForm__staticPreviewActions">

                                    {/* This is the reset button */}
                                    <CreateButton
                                        title={"Reset"}
                                        tooltip={"Reset"}
                                        size="m"
                                        type="button"
                                        onClick={onReset}
                                        disabled={!generatedImage || formSettingsMatchImageSettings || isGenerating || isOptimising}
                                    />

                                    {/* This is the generate button */}
                                    <CreateButton
                                        title={isGenerating ? "Generating..." : generatedImage ? "Regenerate Image" : "Generate Image"}
                                        tooltip={"Generate Image"}
                                        size="m"
                                        type="button"
                                        onClick={() => {

                                            // Reset the selected preview background colour to transparent
                                            setSelectedPreviewBackgroundColour("Transparent");

                                            // Start generation
                                            onGenerate();

                                        }}
                                        disabled={!canGenerate || isOptimising}
                                    />

                                    {/* Show submit on review, otherwise show optimise */}
                                    {currentStep === "review" ? (

                                        <CreateButton
                                            title={"Submit"}
                                            tooltip={"Submit Image as POD Design"}
                                            size="m"
                                            type="button"
                                            onClick={handleSubmitDesign}
                                            disabled={!canSubmit || isGenerating || isOptimising}
                                        />

                                    ) : (

                                        <CreateButton
                                            title={isOptimising ? "Optimising..." : "Optimise for Print"}
                                            tooltip={"Optimise for print"}
                                            size="m"
                                            type="button"
                                            onClick={onOptimise}
                                            disabled={!canOptimise || isGenerating}
                                        />

                                    )}

                                </div>

                            </>

                        );

                    })()}

                </div>


            </div>

        </div>

    );
    
}